import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import { every } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { SelectChangeEvent } from 'typings/events/selection-change-event';

import { InsightsHistogramTooltip } from 'src/app/graph/insights/InsightsHistogramTooltip';
import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import { Heatmap } from 'src/components/charts/Heatmap';
import {
  Histogram,
  usePrepareHistogramData,
} from 'src/components/charts/Histogram';
import { LineChart } from 'src/components/charts/LineChart';
import { Loading } from 'src/components/common/loading/Loading';
import { NoData } from 'src/components/common/noData/NoData';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { DateRangeBadge } from 'src/components/dateRangeBadge/DateRangeBadge';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { format } from 'src/lib/format';
import { Resolution } from 'src/lib/graphqlTypes/types';

import {
  LatencyChartType,
  LatencyPercentileChart,
} from '../../useCurrentField';

import {
  FieldChartData,
  useFieldChartStackInfo,
} from './useFieldChartStackInfo';
import {
  FieldHeatMapStackData,
  useFieldHeatmapStackInfo,
} from './useFieldHeatmapStackInfo';
import {
  FieldHistogramData,
  useFieldHistogramStackInfo,
} from './useFieldHistogramStackInfo';

const PercentileChart = ({
  data,
  chartType,
  resolution,
}: {
  data: FieldChartData[];
  chartType: LatencyPercentileChart;
  resolution: Resolution;
}) => {
  const { stacks } = useFieldChartStackInfo(data, chartType);
  const formatValue = (_: 'major' | 'minor', value: number): string =>
    format.ms(value, 1, false);

  return (
    <LineChart
      className="h-56"
      stacks={stacks}
      resolution={resolution}
      stacked={false}
      formatValue={formatValue}
      tooltipLabelAs={<InsightsLineChartTooltip formatValue={formatValue} />}
      hideCursorArrow
    />
  );
};

const LatencyChart = ({ data }: { data: FieldHistogramData[] }) => {
  const histogramStackInfo = useFieldHistogramStackInfo(data);
  const histogramData = usePrepareHistogramData({
    ...histogramStackInfo,
  });
  return (
    <Histogram
      data={histogramData}
      className="h-56"
      {...histogramStackInfo}
      data-analytics-category="Field Insights"
      data-analytics-label="Latency histogram click"
      tooltipLabelAs={<InsightsHistogramTooltip title="Latency distribution" />}
      hideCursorArrow
    />
  );
};

const LatencyHeatmap = ({
  data,
  resolution,
}: {
  data: FieldHistogramData[];
  resolution: Resolution;
}) => {
  const heatmapStackInfo = useFieldHeatmapStackInfo(
    data as FieldHeatMapStackData[],
  );
  return (
    <Heatmap className="h-56" {...heatmapStackInfo} resolution={resolution} />
  );
};

interface Props {
  loading?: boolean;
  data: FieldHistogramData[] | FieldChartData[];
  fromDate: Date;
  toDate: Date;
  resolution: Resolution;
  showSubscriptionMissingInfo: boolean;
  showClientFiltersWarning: boolean;
  __test?: {
    chartType?: LatencyChartType;
  };
}

const chartTypeToDescription = new Map<LatencyChartType, string>([
  ['heatmap', 'The heatmap showing field latency changes over time.'],
  ['histogram', 'The distribution of field execution latency.'],
  ['p50', 'The 50th percentile of field execution latency over time.'],
  ['p90', 'The 90th percentile of field execution latency over time.'],
  ['p95', 'The 95th percentile of field execution latency over time.'],
]);

export const FieldPageLatencyDistributionChart = ({
  loading,
  data,
  fromDate,
  toDate,
  resolution,
  showSubscriptionMissingInfo,
  showClientFiltersWarning,
  __test,
}: Props) => {
  const [chartType, setChartType] = useState<LatencyChartType>(
    __test?.chartType ?? 'histogram',
  );
  const trackCustomEvent = useTrackCustomEvent();
  const timeRangeSeconds = Math.abs(
    moment.duration(moment(fromDate).diff(moment(toDate))).asSeconds(),
  );
  const showLongTimeRangeWarning =
    timeRangeSeconds > moment.duration(1, 'week').asSeconds();

  const noLatencyData = every(
    data,
    (item: FieldChartData & FieldHistogramData) => {
      return (
        !item.metrics.fieldHistogram.p50 &&
        !item.metrics.fieldHistogram.p90 &&
        !item.metrics.fieldHistogram.p95 &&
        !item.metrics.fieldHistogram.sparseBuckets.length
      );
    },
  );

  return (
    <Card variant="unstyled">
      <CardHeader>
        <CardTitle>
          <Select
            value={chartType}
            onChange={(event: SelectChangeEvent<LatencyChartType>) => {
              trackCustomEvent({
                action: 'user_action',
                category: 'Field Insights',
                label: `Change latency chart mode: ${event.target.value}`,
              });
              setChartType(event.target.value);
            }}
            feel="flat"
            className="self-center font-heading text-xl font-medium text-heading"
            placement="bottom-start"
          >
            <option className="font-normal" value="histogram">
              Latency distribution
            </option>
            <option className="font-normal" value="heatmap">
              Latency heatmap
            </option>
            <option className="font-normal" value="p50">
              P50 latency
            </option>
            <option className="font-normal" value="p90">
              P90 latency
            </option>
            <option className="font-normal" value="p95">
              P95 latency
            </option>
          </Select>
          <DateRangeBadge />
          {showSubscriptionMissingInfo ||
          showClientFiltersWarning ||
          showLongTimeRangeWarning ? (
            <Tooltip
              label={
                <ul
                  className={classnames({
                    'list-inside list-disc':
                      [
                        showLongTimeRangeWarning,
                        showSubscriptionMissingInfo,
                        showClientFiltersWarning,
                      ].filter((b) => !!b).length > 1,
                  })}
                >
                  {showLongTimeRangeWarning && (
                    <li data-testid="latency-graph-timerange-warning">
                      Field latency data is limited to the last 7 days.
                    </li>
                  )}
                  {showSubscriptionMissingInfo && (
                    <li data-testid="latency-graph-subscriptions-warning">
                      This chart does not yet include any latencies for field
                      executions from subscription operations.
                    </li>
                  )}
                  {showClientFiltersWarning && (
                    <li data-testid="latency-graph-clients-warning">
                      Showing data from all clients. This chart does not
                      currently reflect any client filters that have been
                      selected.
                    </li>
                  )}
                </ul>
              }
            >
              <IconWarning
                className="text-icon-warning"
                data-testid="latency-graph-warning-trigger"
              />
            </Tooltip>
          ) : null}
        </CardTitle>
        <CardDescription data-testid="displayed-latency-msg" className="pl-3">
          {chartTypeToDescription.get(chartType)}
        </CardDescription>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex justify-center py-10 text-center">
            <Loading />
          </div>
        ) : noLatencyData ? (
          <NoData
            className="flex h-56 items-center justify-center"
            message="There is no latency data for this field in this time period."
          />
        ) : chartType === 'heatmap' ? (
          <LatencyHeatmap
            data={data as FieldHistogramData[]}
            resolution={resolution}
          />
        ) : chartType === 'histogram' ? (
          <LatencyChart data={data as FieldHistogramData[]} />
        ) : (
          <PercentileChart
            data={data as FieldChartData[]}
            chartType={chartType}
            resolution={resolution}
          />
        )}
      </CardBody>
    </Card>
  );
};
